import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssistants, Assistant } from '../context/AssistantContext';
import { useModeContext } from '../context/ModeContext';
import { templates } from '../data/templates';
import { CreateAssistantWizard } from '../components/CreateAssistantWizard';
import { TemplateLibrary } from '../components/TemplateLibrary';

type Tab = 'my' | 'library';

export function Assistants() {
  const { assistants, loading, error, deleteAssistant, setActiveAssistantId } = useAssistants();
  const [activeTab, setActiveTab] = useState<Tab>('my');
  const [showWizard, setShowWizard] = useState(false);
  const [preselectedTemplateId, setPreselectedTemplateId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setMode } = useModeContext();

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this assistant? This action cannot be undone.')) return;
    setDeletingId(id);
    setActionError(null);
    try {
      await deleteAssistant(id);
    } catch (err) {
      console.error('Failed to delete assistant:', err);
      setActionError('Could not delete the assistant. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleChat = (assistant: Assistant) => {
    if (assistant.status !== 'ready') {
      setActionError(`"${assistant.name}" is not ready yet (status: ${assistant.status}).`);
      return;
    }
    setActionError(null);
    setActiveAssistantId(assistant.id);
    setMode('assistants');
    navigate('/');
  };

  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return 'Custom';
    return templates.find(t => t.id === templateId)?.name || 'Custom';
  };

  const assistantStatusLabel = (status: Assistant['status']) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'error':
        return 'Error';
      case 'creating':
        return 'Creating';
      default:
        return status;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-800 font-heading">Assistants</h1>
        <p className="text-neutral-500 text-sm mt-1">Create and manage custom assistants trained on your knowledge bases.</p>
      </div>

      {/* Sub-tab bar */}
      <div className="flex gap-6 border-b border-neutral-200 mb-6">
        <button
          onClick={() => setActiveTab('my')}
          className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'my'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          My Assistants
        </button>
        <button
          onClick={() => setActiveTab('library')}
          className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'library'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Template Library
        </button>
      </div>

      {/* My Assistants tab */}
      {activeTab === 'my' && (
        <>
          {(error || actionError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-btn text-xs text-red-700" role="alert">
              {actionError || error}
            </div>
          )}
          {loading && assistants.length === 0 ? (
            <div className="text-center py-12 text-neutral-400">Loading assistants...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Create New card */}
              <button
                onClick={() => { setPreselectedTemplateId(null); setShowWizard(true); }}
                className="card border-2 border-dashed border-neutral-300 hover:border-primary-400 hover:bg-primary-50/50 flex flex-col items-center justify-center py-10 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-neutral-100 group-hover:bg-primary-100 flex items-center justify-center mb-3 transition-colors">
                  <svg className="w-5 h-5 text-neutral-400 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-neutral-500 group-hover:text-primary-600">Create New Assistant</span>
              </button>

              {/* Existing assistants */}
              {assistants.map(assistant => (
                <div key={assistant.id} className="card p-5 flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-800">{assistant.name}</h3>
                      <span className="text-xs text-neutral-400 mt-0.5 inline-block">
                        {getTemplateName(assistant.template_id)}
                      </span>
                    </div>
                    <span className={`badge ${assistant.status === 'ready' ? 'badge-green' : assistant.status === 'error' ? 'badge-red' : 'badge-yellow'}`}>
                      {assistantStatusLabel(assistant.status)}
                    </span>
                  </div>

                  {/* KB info */}
                  <div className="text-xs text-neutral-500">
                    {assistant.knowledgeBases && assistant.knowledgeBases.length > 0 ? (
                      <span>
                        {assistant.knowledgeBases.length}{' '}
                        {assistant.knowledgeBases.length === 1 ? 'Knowledge Base' : 'Knowledge Bases'}
                      </span>
                    ) : (
                      <span className="text-neutral-400">No knowledge bases linked</span>
                    )}
                  </div>

                  <div className="text-xs text-neutral-400">
                    Created {new Date(assistant.created_at).toLocaleDateString()}
                  </div>

                  <div className="flex gap-2 mt-auto pt-2 border-t border-neutral-100">
                    <button
                      onClick={() => handleChat(assistant)}
                      disabled={assistant.status !== 'ready'}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Chat
                    </button>
                    <button
                      onClick={() => handleDelete(assistant.id)}
                      disabled={deletingId === assistant.id}
                      className="text-xs text-neutral-400 hover:text-red-500 transition-colors ml-auto"
                    >
                      {deletingId === assistant.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Template Library tab */}
      {activeTab === 'library' && (
        <TemplateLibrary
          onSelectTemplate={(id) => {
            setPreselectedTemplateId(id);
            setShowWizard(true);
          }}
        />
      )}

      {/* Wizard */}
      {showWizard && (
        <CreateAssistantWizard
          preselectedTemplateId={preselectedTemplateId}
          onClose={() => {
            setShowWizard(false);
            setPreselectedTemplateId(null);
          }}
        />
      )}
    </div>
  );
}
